"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { gql, request } from "graphql-request";
import { ENVIO_GRAPHQL_ENDPOINT } from "@/constants/api";

interface Burn {
  id: string;
  amount: string;
  timestamp: string;
  transactionHash: string | null;
  txHash?: string | null;
  from: string;
  source: string;
  blockNumber?: string;
  chainId?: string;
}
interface BurnStats {
  id: string;
  source: string;
  totalBurned: string;
  burnCount: string;
  uniqueBurners?: string;
  chainId?: string;
  firstBurnTime?: string;
  lastBurnTime?: string;
}
interface GlobalBurnStats {
  id: string;
  totalBurnedAllChains: string;
  incineratorBurns: string;
  userBurns: string;
  beratrackrBurns?: string;
  overunderBurns?: string;
  burnCountAllChains?: string;
  uniqueBurners?: string;
  incineratorUniqueBurners?: string;
  lastUpdateTime?: string;
}

const GET_BURNS_QUERY = gql`query GetBurns($limit: Int!, $offset: Int!) { HenloBurn(limit: $limit, offset: $offset, order_by: { timestamp: desc }) { id amount timestamp transactionHash from source blockNumber chainId } }`;
const GET_TOTAL_BURNED_QUERY = gql`query GetTotalBurned { HenloGlobalBurnStats { id totalBurnedAllChains incineratorBurns userBurns beratrackrBurns overunderBurns burnCountAllChains uniqueBurners incineratorUniqueBurners lastUpdateTime } HenloBurnStats { id source totalBurned burnCount uniqueBurners chainId firstBurnTime lastBurnTime } }`;

interface BurnPage {
  burns: Burn[];
  hasMore: boolean;
  nextOffset: number;
}

export function useBurnsInfinite(limit: number = 20) {
  return useInfiniteQuery<BurnPage, Error>({
    queryKey: ["burns", "infinite", limit, "envio"],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = (pageParam as number) * limit;
      const data = await request<{ HenloBurn: Burn[] }>(ENVIO_GRAPHQL_ENDPOINT, GET_BURNS_QUERY, {
        limit,
        offset,
      });
      const burns = data.HenloBurn.filter((burn) => {
        try {
          const amount = BigInt(burn.amount);
          return amount > 0n;
        } catch {
          return false;
        }
      }).map((burn) => ({ ...burn, txHash: burn.transactionHash }));
      return {
        burns,
        hasMore: data.HenloBurn.length === limit,
        nextOffset: (pageParam as number) + 1,
      };
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextOffset : undefined),
    refetchInterval: 5000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    staleTime: 3000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useTotalBurned() {
  return useQuery({
    queryKey: ["burns", "totalBurned", "envio"],
    queryFn: async () => {
      const data = await request<{
        HenloGlobalBurnStats: GlobalBurnStats[];
        HenloBurnStats: BurnStats[];
      }>(ENVIO_GRAPHQL_ENDPOINT, GET_TOTAL_BURNED_QUERY);
      const globalStats = data.HenloGlobalBurnStats[0];
      return {
        totalBurned: globalStats?.totalBurnedAllChains || "0",
        incineratorBurns: globalStats?.incineratorBurns || "0",
        userBurns: globalStats?.userBurns || "0",
        beratrackrBurns: globalStats?.beratrackrBurns || "0",
        overunderBurns: globalStats?.overunderBurns || "0",
        burnCountAllChains: globalStats?.burnCountAllChains || "0",
        uniqueBurners: globalStats?.uniqueBurners || "0",
        incineratorUniqueBurners: globalStats?.incineratorUniqueBurners || "0",
        burnStatsBySource: data.HenloBurnStats || [],
      };
    },
    refetchInterval: 120000,
    staleTime: 60000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useUserBurns(address?: string, limit: number = 10) {
  const USER_BURNS_QUERY = gql`query GetUserBurns($address: String!, $limit: Int!) { HenloBurn(where: { from: { _eq: $address } }, limit: $limit, order_by: { timestamp: desc }) { id amount timestamp transactionHash from source blockNumber chainId } }`;
  return useQuery({
    queryKey: ["burns", "user", address, limit, "envio"],
    queryFn: async () => {
      if (!address) return { burns: [] };
      const data = await request<{ HenloBurn: Burn[] }>(ENVIO_GRAPHQL_ENDPOINT, USER_BURNS_QUERY, {
        address,
        limit,
      });
      const burns = data.HenloBurn.map((burn) => ({ ...burn, txHash: burn.transactionHash }));
      return { burns };
    },
    enabled: !!address,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });
}
